// Referees tab: game tables grouped by referee and day, and what each is paid

import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import GameReport from './GameReport';
import GameReportGroup from './GameReportGroup';
import GameReportSection from './GameReportSection';
import RefereeRateInput from './RefereeRateInput';
import RefereeReportPDF from './RefereeReportPDF';
import RefereePayModalBody from './RefereePayModalBody';
import RefereeRoster from './RefereeRoster';
import { useModal } from './ModalProvider';
import { useGameReport, useRefereeRates, usePayouts } from '../utils/hooks';
import {
  getGamesByDay,
  countGames,
  totalGamesOn,
  totalGamesOf,
  gameRate,
  hasRates,
  payDue,
  sumPay,
  totalPayOn,
  totalPayOf,
  totalPayDueOf,
  moneyStr,
} from '../utils/gameReport';
import { PayoutStatuses, UnpaidPayoutStatuses } from '../utils/enums';

export default function RefereeReport({ id, colorLegend }) {
  const {
    days,
    sortedGames,
    sortedFields,
    gamesReferees,
    hasRefereedDivision,
    getRefereeName,
  } = useGameReport(id);
  // The PDF twin is handed the result: it shows the amounts typed here too
  const {
    getRates,
    isRatesSynced,
    setRefereeRate,
    clearRefereeRate,
    setGameRate,
    clearGameRate,
  } = useRefereeRates(id);
  const { getPaid, payouts, isPayoutsSynced } = usePayouts(id);
  const { openModal } = useModal();

  // PayPal may take a payout and refuse it a minute later, by webhook, when
  // the pay modal is long closed. Pending → failed is the modal's own toast
  const seenStatuses = useRef(null);
  useEffect(() => {
    if (!isPayoutsSynced) return;
    const seen = seenStatuses.current;
    seenStatuses.current = new Map(payouts.map((p) => [p.id, p.status]));
    // The first look is a baseline: old refusals are not news
    if (!seen) return;

    for (const p of payouts) {
      const was = seen.get(p.id);
      const wasTaken =
        !!was &&
        was !== PayoutStatuses.PENDING &&
        !UnpaidPayoutStatuses.includes(was);
      if (!wasTaken || !UnpaidPayoutStatuses.includes(p.status)) continue;

      const name = getRefereeName(p.userId) || 'the referee';
      toast.error(
        `Payout of ${moneyStr(p.amount)} to ${name} failed: ` +
          (p.reason ?? `PayPal marked it ${p.status}`),
        { toastId: p.id, autoClose: false },
      );
    }
  }, [payouts, isPayoutsSynced, getRefereeName]);

  const payReferee = (refereeId, refereeName, amount) => {
    return openModal({
      header: 'Pay referee',
      body: (
        <RefereePayModalBody
          tournamentId={id}
          refereeId={refereeId}
          refereeName={refereeName}
          amount={amount}
        />
      ),
    });
  };

  return (
    <GameReport
      id={id}
      colorLegend={colorLegend}
      documentTitle='Referees'
      pdf={
        <RefereeReportPDF
          id={id}
          colorLegend={colorLegend}
          getRates={getRates}
          isRatesSynced={isRatesSynced}
        />
      }
    >
      <RefereeRoster id={id} getRates={getRates} />
      {gamesReferees.map((referee) => {
        const gamesByDay = getGamesByDay(sortedGames, referee, days);
        const refereeName = getRefereeName(referee);
        const rates = getRates(referee);
        const showPay = isRatesSynced && hasRates(rates);
        const pay = gamesByDay.reduce(
          (total, { games }) => total + sumPay(games, rates),
          0,
        );
        const paid = getPaid(referee);
        const due = payDue(pay, paid);

        return (
          // Keyed by referee, not by position: the list is rebuilt from live
          // game documents, and a reorder would hand a half-typed rate to
          // whoever landed on that index
          <GameReportGroup
            key={referee}
            title={refereeName}
            action={
              <RefereeRateInput
                value={rates?.rate}
                onSet={(rate) => setRefereeRate(referee, rate)}
                onClear={() => clearRefereeRate(referee)}
                disabled={!isRatesSynced}
              />
            }
            total={totalGamesOf(refereeName, countGames(gamesByDay))}
            totalPay={
              showPay &&
              // A referee nobody has paid yet reads as it always has
              (isPayoutsSynced && paid > 0
                ? totalPayDueOf(refereeName, pay, paid, due)
                : totalPayOf(refereeName, pay))
            }
            payAction={
              // Nothing to send when every game of the referee prices to zero
              // or the money has already gone out, and the callable refuses a
              // non-positive amount anyway. What the payouts of the tournament
              // are must be known first: offering the full total over a referee
              // already paid most of it is how one gets paid twice
              showPay &&
              isPayoutsSynced &&
              due > 0 && (
                <button onClick={() => payReferee(referee, refereeName, due)}>
                  Pay
                </button>
              )
            }
          >
            {gamesByDay.map(({ day, games }, dayIndex) => (
              <GameReportSection
                key={dayIndex}
                title={day.format('MMMM D, YY')}
                total={totalGamesOn(day.format('MMMM D'), games.length)}
                totalPay={
                  showPay &&
                  totalPayOn(day.format('MMMM D'), sumPay(games, rates))
                }
                games={games}
                fields={sortedFields}
                colorLegend={colorLegend}
                noDivisionCol={!hasRefereedDivision}
                noGameCol
                noScoreCol
                noRefereeCol
                renderRate={(game) => (
                  <RefereeRateInput
                    value={gameRate(rates, game.id)}
                    isDefault={rates?.exceptions?.[game.id] === undefined}
                    onSet={(rate) => setGameRate(referee, game.id, rate)}
                    onClear={() => clearGameRate(referee, game.id)}
                    disabled={!isRatesSynced}
                  />
                )}
              />
            ))}
          </GameReportGroup>
        );
      })}
    </GameReport>
  );
}
